import { ConnectError } from '@bufbuild/connect';
import { useEffect } from 'react';
import { FieldValues, UseFormSetError } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { generatePath, useNavigate } from 'react-router';

import {
  PATHS,
  ToastErrors,
  toastErrors,
  UserBadRequestError,
} from '@/shared/config';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { debug, getName, safeJSONParse } from '@/shared/lib';
import { toastCaller } from '@/shared/ui';

import { accessModel } from '@/entities/access';
import { authModel } from '@/entities/auth';
import { PATH_TO_PATIENTS_SHARED_WITH_ME_LIST } from '@/entities/patient';
import { userModel } from '@/entities/user';

import { useLogout } from '@/features/logout';
import { useAcceptSharingPatientInvite } from '@/features/sharePatient/hooks/useAcceptSharingPatientInvite';

import {
  hasForcedLogoutBeenAttempted,
  isInvitationAddressedToCurrentUser,
  markForcedLogoutAttempted,
} from '../lib';

export const useOrganizationInvite = (invitationToken: string) => {
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();
  const navigate = useNavigate();

  const currentUser = useAppSelector(userModel.selectors.selectCurrentUser);
  const currentUserID = useAppSelector(userModel.selectors.selectCurrentUserID);

  const logoutAndReopenInvitation = useLogout({
    eventName: 'Logout ForeignInvitation',
    notice: 'foreignInvitation',
    redirectTo: generatePath(PATHS.organization_invite, {
      token: invitationToken,
    }),
  });

  const handleGetInvitationByToken = async (token: string) => {
    try {
      const { Result, Organization } = await dispatch(
        accessModel.thunks.getInvitationByToken({ Token: token }),
      ).unwrap();

      if (Result.case === 'Sharing') {
        dispatch(accessModel.sharingActions.setOne(Result.value));
      } else if (Result.case === 'OrganizationStaff') {
        navigate(PATHS.patients, {
          state: {
            invitationOrganizationID: Result.value.OrganizationID,
            invitationOrganizationName: Organization?.Name,
          },
        });
      }
    } catch (error) {
      navigate(PATHS.patients);
    }
  };

  // NOTE: `CheckInviteToken` rather than `GetInvitationByToken` — it is the only
  // invitation endpoint that is not actor-checked, so it also answers for a
  // session that does not belong to the recipient.
  const isForeignInvitation = async (token: string) => {
    try {
      const { Info } = await dispatch(
        authModel.thunks.checkInviteToken({ Token: token }),
      ).unwrap();

      return (
        Info.case === 'InvitationInfo' &&
        !isInvitationAddressedToCurrentUser(Info.value, {
          id: currentUserID,
          emails: currentUser.PersonalData?.Emails ?? [],
        })
      );
    } catch (error) {
      debug('Failed to check the invitation token', error);

      return false;
    }
  };

  // NOTE: the link is opened from a mailbox, usually in the browser where the
  // sender is still signed in, and `GetInvitationByToken` answers 403 to anyone
  // but the recipient. Dropping the session hands the link over to the anonymous
  // sign-up/sign-in flow, which is where an invited person belongs.
  const initializeInvitation = async (token: string) => {
    const shouldDropSession =
      (await isForeignInvitation(token)) &&
      !hasForcedLogoutBeenAttempted(token);

    if (shouldDropSession) {
      markForcedLogoutAttempted(token);

      try {
        await logoutAndReopenInvitation();

        return;
      } catch (error) {
        debug('Failed to drop the session for a foreign invitation', error);
      }
    }

    await handleGetInvitationByToken(token);
  };

  // initialize the invite
  useEffect(() => {
    initializeInvitation(invitationToken);
    // Only re-key on the token: initializeInvitation is redefined per render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invitationToken]);

  const invitation = useAppSelector(
    accessModel.sharingSelectors.selectSharingInvitationByToken(
      invitationToken,
    ),
  );
  const userLocale = useAppSelector(userModel.selectors.selectUserLocale);

  const invitationStatus = invitation?.Status;

  const invitationCreatedAt = invitation?.Created?.At;

  const senderFullName = getName(
    invitation?.TargetDetails?.SenderFirstName,
    invitation?.TargetDetails?.SenderLastName,
    { userLocale },
  );

  const acceptSharingPatientInvite = useAcceptSharingPatientInvite();

  const navigateForAcceptedInvite = () => {
    if (invitation?.PatientID) {
      navigate({
        pathname: generatePath(PATHS.patientProfile_DEPRECATED, {
          patientID: invitation.PatientID,
        }),
      });
    } else if (invitation?.OrganizationID) {
      navigate(PATHS.patients, {
        state: {
          invitationOrganizationID: invitation.OrganizationID,
          invitationOrganizationName:
            invitation.TargetDetails?.OrganizationName,
        },
      });
    }
  };

  const navigateToSharedWithMeList = () => {
    navigate(PATH_TO_PATIENTS_SHARED_WITH_ME_LIST);
  };

  const acceptSharingPatientInviteHandle = async (
    accessCode: string,
    setError: UseFormSetError<FieldValues>,
  ) => {
    try {
      if (invitation) {
        await acceptSharingPatientInvite({
          invitationID: invitation.ID,
          accessCode,
        });
      }

      navigateForAcceptedInvite();
    } catch (error) {
      if (error instanceof ConnectError) {
        const parsedMessage = safeJSONParse(error.rawMessage);
        const errorCode = parsedMessage?.code;

        if (errorCode === UserBadRequestError.INCORRECT_INVITATION_SECRET) {
          setError('accessCode', {
            message: formatMessage({
              id: 'addSharingPatient.error.incorrectAccessCode',
              defaultMessage:
                'Access code is incorrect. Contact the sender to receive the correct access code.',
            }),
          });
        } else if (
          errorCode ===
          UserBadRequestError.INVITATION_SECRET_INPUT_LIMIT_EXCEEDED
        ) {
          setError('accessCode', {
            type: 'canceledCode',
            message: formatMessage({
              id: 'addSharingPatient.error.canceledAccessCode',
              defaultMessage:
                'You have entered access code too many times. Your access to the patient has been canceled. Contact the sender to send you a patient profile once again.',
            }),
          });
        }
      }
    }
  };

  // NOTE: This is not an error in the direct sense; it is a message about the cancellation of the invitation
  const showCancelledInvitationToast = () => {
    const errorHeading = formatMessage(toastErrors[ToastErrors.errorHeading]);

    toastCaller({
      type: 'error',
      heading: errorHeading,
      message: formatMessage({
        id: 'addSharingPatient.error.noAccess',
        defaultMessage:
          'The invitation has been canceled. You no longer have access to the patient.',
      }),
      autoClose: 10000,
    });
  };

  return {
    invitationStatus,
    senderFullName,
    invitationCreatedAt,
    acceptSharingPatientInviteHandle,
    navigateForAcceptedInvite,
    navigateToSharedWithMeList,
    showCancelledInvitationToast,
  };
};
